<template>
  <div class="flex items-center justify-center p-0 w-full"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop">
    <!-- Drag Overlay -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDragging"
        class="fixed inset-0 z-[100] bg-[#2EB2A0]/95 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="bg-white/20 p-8 rounded-full mb-8 backdrop-blur-sm shadow-2xl">
           <span class="material-symbols-outlined text-white text-[100px]">rotate_right</span>
        </div>
        <h2 class="text-white text-5xl font-bold tracking-tight drop-shadow-sm">Drop PDF file to Rotate</h2>
      </div>
    </transition>

    <!-- Dropzone -->
    <div 
      v-if="pages.length === 0" 
      @click="fileInput.click()" 
      class="w-full max-w-[1000px] mt-4 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center text-center relative group p-0"
    >
      <div class="absolute -inset-1 bg-gradient-to-r from-[#2EB2A0] to-[#2EB2A0]/60 rounded-2xl blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
      <div class="relative w-full h-[360px] bg-white border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center gap-6 transition-all hover:border-[#2EB2A0]/50 p-6">
        <div class="size-20 bg-[#2EB2A0]/10 rounded-full flex items-center justify-center text-[#2EB2A0] mt-2">
           <span class="material-symbols-outlined text-[48px]">rotate_right</span>
        </div>
        
        <div class="space-y-1">
            <h3 class="text-2xl font-bold text-[#16304D]">Select or Drop PDF File to Rotate</h3>
            <p class="text-sm font-medium text-slate-500">Rotate PDF pages 90°, 180° or 270° permanently and download</p>
        </div>

        <button @click.stop="fileInput.click()" class="bg-[#2EB2A0] text-white text-base font-semibold px-8 py-3 rounded-xl hover:bg-[#279B8C] shadow-xl shadow-[#2EB2A0]/20 transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center gap-2">
            <span class="material-symbols-outlined">add_circle</span>
            <span>Select PDF File</span>
        </button>

        <div class="flex flex-wrap justify-center items-center gap-4 text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1">
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">lock</span> Encrypted &amp; Secure</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">sync</span> Rotate All or Individual</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">download</span> Instant Download</span>
        </div>
      </div>
    </div>

    <!-- Main Rotate Workspace -->
    <div v-else class="fixed inset-0 top-0 z-50 flex flex-col h-screen bg-[#F3F4F6]">
        <!-- Top Navigation Bar -->
        <header class="flex items-center justify-between whitespace-nowrap border-b border-slate-200 bg-white px-4 md:px-6 py-3 shrink-0 shadow-sm z-20">
            <div class="flex items-center gap-3 md:gap-4">
                <a href="/" class="flex items-center gap-2">
                     <img :src="'/logo.png'" alt="Logo" class="h-8 md:h-10 w-auto" @error="(e) => e.target.style.display='none'">
                     <span class="font-bold text-[#16304D] text-lg hidden sm:inline">Rotate PDF</span>
                </a>
                <div class="h-6 w-px bg-slate-200 hidden sm:block"></div>
                <div>
                    <div class="flex items-center gap-2 text-xs md:text-sm text-slate-600 font-medium">
                        <span class="font-bold text-[#16304D] truncate max-w-[200px]">{{ currentFileName }}</span>
                        <span class="text-slate-300">&middot;</span>
                        <span class="font-bold text-[#2EB2A0]">{{ pages.length }} Pages</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 md:gap-4">
                 <button @click="rotateAll(-90)" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-[#2EB2A0]/10 text-[#2EB2A0] border border-[#2EB2A0]/30 text-sm font-semibold hover:bg-[#2EB2A0]/20 transition-colors">
                    <span class="material-symbols-outlined text-lg">rotate_left</span>
                    <span class="hidden md:inline">Rotate All Left</span>
                </button>
                 <button @click="rotateAll(90)" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-[#2EB2A0]/10 text-[#2EB2A0] border border-[#2EB2A0]/30 text-sm font-semibold hover:bg-[#2EB2A0]/20 transition-colors">
                    <span class="material-symbols-outlined text-lg">rotate_right</span>
                    <span class="hidden md:inline">Rotate All Right</span>
                </button>
                <button @click="resetAll" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-red-50 border border-red-200 text-sm font-semibold hover:bg-red-100 text-red-600 transition-colors">
                    <span class="material-symbols-outlined text-lg">restart_alt</span>
                    <span class="hidden md:inline">Start Over</span>
                </button>
            </div>
        </header>

        <div class="flex flex-1 overflow-hidden relative">
            <!-- Grid Workspace -->
            <main class="flex-1 overflow-y-auto bg-[#F3F4F6] p-4 md:p-8 relative">
                <div class="max-w-7xl mx-auto pb-24">
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                         <div 
                            v-for="page in pages" 
                            :key="page.uid" 
                            class="thumbnail-card relative select-none group"
                         >
                            <div class="h-60 bg-white rounded-xl border-2 border-slate-200 relative transition-all overflow-hidden hover:shadow-xl">
                                 <!-- Rotation Degree Badge -->
                                 <div class="absolute top-3 left-3 bg-[#16304D] text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow z-30">
                                     {{ page.rotation }}°
                                 </div>

                                 <!-- Individual Rotation Controls -->
                                 <div class="absolute top-3 right-3 flex items-center gap-1 z-30">
                                     <button @click="rotatePage(page, -90)" class="size-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-700 hover:bg-[#2EB2A0] hover:text-white shadow transition-colors" title="Rotate 90° Left">
                                         <span class="material-symbols-outlined text-[18px]">rotate_left</span>
                                     </button>
                                     <button @click="rotatePage(page, 90)" class="size-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-700 hover:bg-[#2EB2A0] hover:text-white shadow transition-colors" title="Rotate 90° Right">
                                         <span class="material-symbols-outlined text-[18px]">rotate_right</span>
                                     </button>
                                 </div>

                                 <!-- Thumbnail -->
                                 <div class="absolute inset-0 flex items-center justify-center p-3 bg-slate-50">
                                      <img 
                                         v-show="!page.isLoading && page.thumbnail"
                                         :src="page.thumbnail" 
                                         class="max-h-full max-w-full object-contain shadow-sm rounded transition-transform duration-300"
                                         :style="{ transform: `rotate(${page.rotation}deg)` }"
                                         draggable="false"/>

                                      <div v-if="page.isLoading" class="flex flex-col items-center justify-center">
                                          <div class="animate-spin rounded-full h-8 w-8 border-4 border-[#2EB2A0]/30 border-t-[#2EB2A0] mb-2"></div>
                                          <span class="text-[10px] font-bold text-[#2EB2A0]">Rendering...</span>
                                      </div>
                                 </div>

                                 <!-- Footer Label -->
                                 <div class="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-3 py-2 flex justify-between items-center text-xs font-bold text-[#16304D] z-20">
                                     <span>Page {{ page.pageNum }}</span>
                                     <button @click="rotatePage(page, 180)" class="text-[10px] text-[#2EB2A0] hover:underline flex items-center gap-1">
                                         <span class="material-symbols-outlined text-xs">sync</span> 180° Flip
                                     </button>
                                 </div>
                            </div>
                         </div>
                    </div>
                </div>
            </main>

            <!-- Sidebar Actions -->
            <aside class="w-80 border-l border-slate-200 bg-white flex flex-col shrink-0 z-10 shadow-lg hidden md:flex">
                <div class="p-6 flex flex-col flex-1 gap-6 overflow-y-auto">
                    <div>
                        <h3 class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Rotate Actions</h3>
                        <div class="space-y-3">
                            <button @click="rotateAll(90)" class="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all font-semibold text-slate-700 text-sm">
                                <span class="material-symbols-outlined text-[#2EB2A0]">rotate_right</span>
                                Rotate All 90° Clockwise
                            </button>
                            <button @click="rotateAll(-90)" class="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all font-semibold text-slate-700 text-sm">
                                <span class="material-symbols-outlined text-[#2EB2A0]">rotate_left</span>
                                Rotate All 90° Counter-clockwise
                            </button>
                            <button @click="rotateAll(180)" class="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all font-semibold text-slate-700 text-sm">
                                <span class="material-symbols-outlined text-[#2EB2A0]">sync</span>
                                Flip All Pages 180°
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Download Bar -->
                <div class="p-6 bg-slate-50 border-t border-slate-200">
                    <div class="mb-4">
                        <p class="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Document Status</p>
                        <p class="text-lg font-bold text-[#16304D]">{{ pages.length }} Pages Rotated</p>
                    </div>

                    <button @click="saveAndDownload" :disabled="isDownloading || pages.length === 0" class="w-full bg-[#2EB2A0] text-white h-12 rounded-xl text-base font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-[#279B8C] transition-all disabled:opacity-50">
                        <span v-if="isDownloading" class="animate-spin material-symbols-outlined">progress_activity</span>
                        <span v-else class="material-symbols-outlined">download</span>
                        <span>{{ isDownloading ? 'Saving Rotated PDF...' : 'Save & Download PDF' }}</span>
                    </button>
                </div>
            </aside>
        </div>
    </div>

    <!-- Hidden Input -->
    <input type="file" ref="fileInput" accept="application/pdf" class="hidden" @change="onFileChange" />
  </div>
</template>

<script setup>
import { ref, nextTick, shallowRef, markRaw } from 'vue'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const pages = ref([])
const pdfLibDoc = shallowRef(null)
const pdfjsDoc = shallowRef(null)
const currentFileName = ref('')
const isDragging = ref(false)
const isDownloading = ref(false)
const fileInput = ref(null)

let uidCounter = 0

const processFile = async (file) => {
  if (file.type !== 'application/pdf') return
  currentFileName.value = file.name
  const buffer = await file.arrayBuffer()
  
  const loadedPdfLib = await PDFDocument.load(buffer)
  pdfLibDoc.value = markRaw(loadedPdfLib)
  
  try {
    const uint8 = new Uint8Array(buffer.slice(0))
    const loadedPdfjs = await pdfjsLib.getDocument({ data: uint8 }).promise
    pdfjsDoc.value = markRaw(loadedPdfjs)
  } catch (e) {
    console.error('PDFJS load error:', e)
  }

  const totalP = pdfjsDoc.value ? pdfjsDoc.value.numPages : pdfLibDoc.value.getPageCount()
  const newPages = []
  for (let i = 1; i <= totalP; i++) {
    newPages.push({
      uid: uidCounter++,
      pageNum: i,
      rotation: 0,
      thumbnail: '',
      isLoading: true
    })
  }

  pages.value = newPages
  renderThumbnailsBackground()
}

const renderThumbnailsBackground = async () => {
  for (const pageObj of pages.value) {
    try {
      await new Promise(resolve => setTimeout(resolve, 30))
      if (pdfjsDoc.value) {
        const pdfPage = await pdfjsDoc.value.getPage(pageObj.pageNum)
        const viewport = pdfPage.getViewport({ scale: 1.5, rotation: 0 })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        await pdfPage.render({ canvasContext: ctx, viewport }).promise
        pageObj.thumbnail = canvas.toDataURL('image/png')
      }
      pageObj.isLoading = false
    } catch (e) {
      pageObj.isLoading = false
    }
  }
}

const rotatePage = (page, deg) => {
  page.rotation = (page.rotation + deg + 360) % 360
}

const rotateAll = (deg) => {
  pages.value.forEach(p => p.rotation = (p.rotation + deg + 360) % 360)
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true
    await nextTick()

    const rotatedPdf = await PDFDocument.create()
    for (const page of pages.value) {
      const [copiedPage] = await rotatedPdf.copyPages(pdfLibDoc.value, [page.pageNum - 1])
      if (page.rotation !== 0) {
        copiedPage.setRotation(degrees(page.rotation))
      }
      rotatedPdf.addPage(copiedPage)
    }

    const bytes = await rotatedPdf.save()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `rotated-${currentFileName.value || 'document.pdf'}`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    alert('Error saving rotated PDF: ' + e.message)
  } finally {
    isDownloading.value = false
  }
}

const onFileChange = (e) => {
  if (e.target.files.length) processFile(e.target.files[0])
  e.target.value = ''
}

const resetAll = () => {
  pages.value = []
  pdfLibDoc.value = null
  pdfjsDoc.value = null
  currentFileName.value = ''
}

const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; }
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; }
const onDragOver = (e) => { e.preventDefault(); }
const onDrop = (e) => {
  e.preventDefault()
  isDragging.value = false
  if (e.dataTransfer.files.length) processFile(e.dataTransfer.files[0])
}
</script>
